import React, { useCallback, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import apiService from "../apiService";

// Loaded once per page, not per render: loadStripe injects a <script> and repeated calls
// would add more.
const KEY =
  import.meta.env.VITE_STRIPE_PUBLIC_KEY_LIVE ||
  import.meta.env.VITE_STRIPE_PUBLIC_KEY_TEST ||
  "";
const stripePromise = KEY ? loadStripe(KEY) : null;

/**
 * Apple Pay / Google Pay / Link, as real buttons on our own page.
 *
 * NOTHING CARD-SHAPED PASSES THROUGH US. The buttons are rendered inside Stripe's own
 * cross-origin iframe, and a wallet hands back an opaque confirmation token — never a card
 * number. Our server receives that token id and a price id, which is what keeps this in the
 * same PCI bracket as the hosted redirect it sits beside.
 *
 * The card button next to this is deliberately NOT here: it keeps going to hosted Checkout,
 * so if anything on this path fails there is still a way to pay.
 */
function Buttons({ userId, planId, billingPeriod, withdrawalWaiver, amountCents, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const onConfirm = useCallback(async () => {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw new Error(submitError.message);

      // A confirmation token is the wallet's result. It is the only payment value that leaves
      // the browser, and it is useless to anyone but Stripe.
      const { error: tokenError, confirmationToken } =
        await stripe.createConfirmationToken({ elements });
      if (tokenError) throw new Error(tokenError.message);

      const res = await apiService.startWalletSubscription({
        user_id: userId,
        plan_id: planId,
        billing_period: billingPeriod,
        confirmation_token: confirmationToken.id,
        withdrawal_waiver: !!withdrawalWaiver,
      });

      // 3-D Secure: the bank wants the customer to approve. Stripe puts up its own sheet.
      if (res.status === "requires_action" && res.client_secret) {
        const { error: actionError } = await stripe.handleNextAction({
          clientSecret: res.client_secret,
        });
        if (actionError) throw new Error(actionError.message);
      }
      onSuccess?.(res);
    } catch (e) {
      onError?.(e?.message || "That payment could not be completed.");
    } finally {
      setBusy(false);
    }
  }, [stripe, elements, busy, userId, planId, billingPeriod, onSuccess, onError]);

  return (
    <ExpressCheckoutElement
      onConfirm={onConfirm}
      options={{
        buttonHeight: 48,
        // Apple Pay and Google Pay ONLY. Link is switched off deliberately: it is a Stripe
        // account feature rather than a wallet the visitor already has, and its green button
        // outshouted the primary action while the two real wallets were unavailable.
        paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", paypal: "never" },
        layout: { maxColumns: 1, maxRows: 3 },
      }}
    />
  );
}

export default function WalletButtons({
  userId,
  planId,
  billingPeriod,
  withdrawalWaiver,
  amountCents,
  onSuccess,
  onError,
}) {
  // `mode: 'subscription'` is the deferred-intent flow: no PaymentIntent exists yet, so the
  // amount is declared here purely so the wallet sheet shows the right figure. The real charge
  // is created server-side after the token comes back.
  const options = useMemo(
    () => ({
      mode: "subscription",
      amount: amountCents,
      currency: "usd",
      paymentMethodCreation: "manual",
      appearance: { theme: "night", variables: { borderRadius: "12px" } },
    }),
    [amountCents]
  );

  if (!stripePromise) return null; // no publishable key configured — card button still works

  return (
    <Elements stripe={stripePromise} options={options}>
      <Buttons
        userId={userId}
        planId={planId}
        billingPeriod={billingPeriod}
        withdrawalWaiver={withdrawalWaiver}
        amountCents={amountCents}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}
